import { useEffect, useRef, useState } from 'react';
import { createChart, ColorType, CandlestickSeries } from 'lightweight-charts';
import { candleWs, type CandleData } from '../services/candleWebsocket';
import { formatPrice } from '../utils/formatters';

interface TradingChartProps {
  symbol: string;
  dexIndex?: string;
  currentPrice?: number; // Real current price from market data
  szDecimals?: number; // Size decimals from market metadata
}

export function TradingChart({ symbol, dexIndex, currentPrice }: TradingChartProps) {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<any | null>(null);
  const seriesRef = useRef<any | null>(null);
  const pollIntervalRef = useRef<number | null>(null);
  const [selectedInterval, setSelectedInterval] = useState<string>('1h');
  const isLoadingMoreRef = useRef(false);
  const oldestTimeRef = useRef<number | null>(null);

  // Fetch older candlestick data for pagination
  const fetchOlderCandles = async () => {
    if (!symbol || !oldestTimeRef.current || isLoadingMoreRef.current) return;

    isLoadingMoreRef.current = true;
    console.log('Fetching older candles before:', new Date(oldestTimeRef.current));

    try {
      const intervalMs: Record<string, number> = {
        '1m': 60 * 1000,
        '5m': 5 * 60 * 1000,
        '15m': 15 * 60 * 1000,
        '1h': 60 * 60 * 1000,
        '4h': 4 * 60 * 60 * 1000,
        '1d': 24 * 60 * 60 * 1000,
        '1w': 7 * 24 * 60 * 60 * 1000,
      };

      const candleMs = intervalMs[selectedInterval] || intervalMs['1h'];
      const numCandles = 200;

      const endTime = oldestTimeRef.current - 1; // Just before oldest candle
      const startTime = endTime - (numCandles * candleMs);

      const request = {
        type: 'candleSnapshot',
        req: {
          coin: symbol,
          interval: selectedInterval,
          startTime,
          endTime,
        },
      };

      const response = await fetch('https://api.hyperliquid.xyz/info', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      });

      let candles = null;

      if (!response.ok && symbol.includes(':')) {
        // Fallback to native market if HIP-3 failed
        const baseCoin = symbol.split(':')[1];
        console.log(`Trying native ${baseCoin} for older candles`);

        const fallbackRequest = {
          type: 'candleSnapshot',
          req: {
            coin: baseCoin,
            interval: selectedInterval,
            startTime,
            endTime,
          },
        };

        const fallbackResponse = await fetch('https://api.hyperliquid.xyz/info', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(fallbackRequest),
        });

        if (fallbackResponse.ok) {
          candles = await fallbackResponse.json();
        }
      } else if (response.ok) {
        candles = await response.json();
      }

      if (!candles || candles.length === 0) {
        console.log('No older candles available');
        isLoadingMoreRef.current = false;
        return;
      }

      const transformed = candles.map((c: any) => ({
        time: Math.floor(c.t / 1000),
        open: parseFloat(c.o),
        high: parseFloat(c.h),
        low: parseFloat(c.l),
        close: parseFloat(c.c),
      }));

      if (seriesRef.current && transformed.length > 0) {
        // Get existing data
        const existingData: any[] = [];
        const dataIterator = seriesRef.current.data();
        for (let i = 0; i < dataIterator.length; i++) {
          existingData.push(dataIterator[i]);
        }

        // Prepend older candles
        const allData = [...transformed, ...existingData];
        seriesRef.current.setData(allData);

        // Update oldest time
        oldestTimeRef.current = transformed[0].time * 1000;
        console.log(`Loaded ${transformed.length} older candles`);
      }
    } catch (error) {
      console.error('Error fetching older candles:', error);
    } finally {
      isLoadingMoreRef.current = false;
    }
  };

  // Fetch candlestick data from Hyperliquid
  const fetchCandlestickData = async (interval: string = selectedInterval) => {
    if (!symbol) {
      console.log('No symbol provided');
      return generateSampleData();
    }

    try {
      console.log('Fetching candles for:', symbol, 'interval:', interval, 'dex:', dexIndex);

      // Calculate appropriate time range based on interval
      const intervalMs: Record<string, number> = {
        '1m': 60 * 1000,
        '5m': 5 * 60 * 1000,
        '15m': 15 * 60 * 1000,
        '1h': 60 * 60 * 1000,
        '4h': 4 * 60 * 60 * 1000,
        '1d': 24 * 60 * 60 * 1000,
        '1w': 7 * 24 * 60 * 60 * 1000,
      };

      const candleMs = intervalMs[interval] || intervalMs['1h'];
      const numCandles = 200; // Request 200 candles

      // API expects timestamps in MILLISECONDS
      const endTime = Date.now();
      const startTime = endTime - (numCandles * candleMs);

      // For HIP-3 markets, use the full symbol (e.g., "xyz:AMZN")
      // For native markets, use just the coin name (e.g., "BTC")
      const request = {
        type: 'candleSnapshot',
        req: {
          coin: symbol,
          interval: interval,
          startTime,
          endTime,
        },
      };

      console.log('Candle request:', JSON.stringify(request, null, 2));

      const response = await fetch('https://api.hyperliquid.xyz/info', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        console.warn(`Candle API failed for ${symbol}, status: ${response.status}`);

        // Fallback: If HIP-3 market failed, try native market
        if (symbol.includes(':')) {
          const baseCoin = symbol.split(':')[1];
          console.log(`Trying native market fallback: ${baseCoin}`);

          const fallbackRequest = {
            type: 'candleSnapshot',
            req: {
              coin: baseCoin,
              interval: '1h',
              startTime,
              endTime,
            },
          };

          const fallbackResponse = await fetch('https://api.hyperliquid.xyz/info', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(fallbackRequest),
          });

          if (fallbackResponse.ok) {
            const fallbackCandles = await fallbackResponse.json();
            if (fallbackCandles && fallbackCandles.length > 0) {
              console.log(`Using native ${baseCoin} candles:`, fallbackCandles.length);
              const transformed = fallbackCandles.map((c: any) => ({
                time: Math.floor(c.t / 1000),
                open: parseFloat(c.o),
                high: parseFloat(c.h),
                low: parseFloat(c.l),
                close: parseFloat(c.c),
              }));
              return transformed;
            }
          }
        }

        // If all API attempts fail, fall back to sample data
        console.log('All candle API attempts failed, using sample data');
        return generateSampleData();
      }

      const candles = await response.json();

      if (!candles || candles.length === 0) {
        console.log('API returned empty candles, using sample data');
        return generateSampleData();
      }

      console.log(`Successfully fetched ${candles.length} candles for ${symbol}`);

      // Transform to lightweight-charts format
      // Response format: {t: timestamp_ms, o: open, h: high, l: low, c: close, v: volume}
      const transformed = candles.map((c: any) => ({
        time: Math.floor(c.t / 1000), // Convert API milliseconds to seconds for chart
        open: parseFloat(c.o),
        high: parseFloat(c.h),
        low: parseFloat(c.l),
        close: parseFloat(c.c),
      }));

      // Track oldest timestamp for pagination
      if (transformed.length > 0) {
        oldestTimeRef.current = transformed[0].time * 1000; // Store in milliseconds
      }

      return transformed;
    } catch (error) {
      console.error('Error fetching candlestick data:', error);
      // Fallback to sample data
      return generateSampleData();
    }
  };

  // Generate sample data as fallback
  const generateSampleData = () => {
    const data = [];
    const now = Math.floor(Date.now() / 1000);

    // Use real current price if available, otherwise estimate
    const basePrice = currentPrice || (
      symbol.includes('GOLD') ? 2500 :
      symbol.includes('BTC') ? 50000 :
      symbol.includes('ETH') ? 2500 :
      symbol.includes('TSLA') ? 200 :
      symbol.includes('XYZ') ? 100 :
      symbol.includes('BABA') ? 85 :
      1000
    );

    console.log('Generating sample data with base price:', basePrice);

    for (let i = 100; i >= 0; i--) {
      const time = (now - i * 3600);
      const randomWalk = (Math.random() - 0.5) * (basePrice * 0.02);
      const open = basePrice + randomWalk;
      const close = open + (Math.random() - 0.5) * (basePrice * 0.02);
      const high = Math.max(open, close) + Math.random() * (basePrice * 0.01);
      const low = Math.min(open, close) - Math.random() * (basePrice * 0.01);

      data.push({ time, open, high, low, close });
    }
    return data;
  };

  // Initialize chart once
  useEffect(() => {
    if (!chartContainerRef.current) return;

    // Create chart with professional dark theme
    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: '#0e0f10' },
        textColor: '#6c7884',
      },
      grid: {
        vertLines: { color: '#1a1a1a', style: 1, visible: true },
        horzLines: { color: '#1a1a1a', style: 1, visible: true },
      },
      width: chartContainerRef.current.clientWidth,
      height: 593,
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
        borderColor: '#2d3748',
        rightOffset: 12,
        barSpacing: 3,
        fixLeftEdge: false,
        fixRightEdge: false,
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: true,
      },
      handleScale: {
        axisPressedMouseMove: true,
        mouseWheel: false, // Disabled to use custom mouse-centered zoom
        pinch: true,
      },
      kineticScroll: {
        mouse: false,
        touch: true,
      },
      rightPriceScale: {
        borderColor: '#2d3748',
        scaleMargins: {
          top: 0.08,
          bottom: 0.08,
        },
        autoScale: true,
        minimumWidth: 80,
      },
      localization: {
        priceFormatter: (price: number) => {
          return formatPrice(price, symbol);
        },
      },
      crosshair: {
        mode: 1,
        vertLine: {
          color: '#3d444d',
          width: 1,
          style: 3,
          labelBackgroundColor: '#2962ff',
        },
        horzLine: {
          color: '#3d444d',
          width: 1,
          style: 3,
          labelBackgroundColor: '#2962ff',
        },
      },
    });

    // Create candlestick series using v5 API
    const candlestickSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#0ecb81',
      downColor: '#f6465d',
      borderUpColor: '#0ecb81',
      borderDownColor: '#f6465d',
      wickUpColor: '#0ecb81',
      wickDownColor: '#f6465d',
    });

    chartRef.current = chart;
    seriesRef.current = candlestickSeries;

    // Custom mouse-centered zoom handler
    const handleWheel = (event: WheelEvent) => {
      // Only handle when Ctrl/Cmd is not pressed (for zoom, not scroll)
      if (event.ctrlKey || event.metaKey) return;

      const container = chartContainerRef.current;
      if (!container || !chartRef.current) return;

      // Prevent default scroll behavior
      event.preventDefault();

      const timeScale = chartRef.current.timeScale();
      const visibleRange = timeScale.getVisibleLogicalRange();

      if (!visibleRange) return;

      // Get mouse position relative to chart
      const rect = container.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;

      // Convert mouse X position to logical coordinate (0 to 1, where 0 is left edge, 1 is right edge)
      const chartWidth = rect.width;
      const mouseRatio = mouseX / chartWidth;

      // Calculate current range size
      const currentRange = visibleRange.to - visibleRange.from;

      // Zoom factor (negative deltaY = zoom in, positive = zoom out)
      const zoomSpeed = 0.0005;
      const zoomFactor = 1 + (event.deltaY * zoomSpeed);
      const newRange = currentRange * zoomFactor;

      // Calculate new visible range centered on mouse position
      const pointAtMouse = visibleRange.from + (currentRange * mouseRatio);
      const newFrom = pointAtMouse - (newRange * mouseRatio);
      const newTo = pointAtMouse + (newRange * (1 - mouseRatio));

      // Apply the new range
      timeScale.setVisibleLogicalRange({
        from: newFrom,
        to: newTo,
      });
    };

    const container = chartContainerRef.current;
    container.addEventListener('wheel', handleWheel, { passive: false });

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
        });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      container.removeEventListener('wheel', handleWheel);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      chart.remove();
    };
  }, []);

  // Fetch and update chart data when symbol changes
  useEffect(() => {
    if (!seriesRef.current || !symbol) {
      console.log('Chart not ready or no symbol:', { hasChart: !!seriesRef.current, symbol });
      return;
    }

    console.log('Loading chart data for symbol:', symbol, 'dex:', dexIndex);

    // Clear existing polling interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    // Load initial data
    const loadData = async () => {
      const data = await fetchCandlestickData();
      console.log('Loaded data points:', data.length);
      if (seriesRef.current && data.length > 0) {
        // Update price formatter with new symbol
        if (chartRef.current) {
          chartRef.current.applyOptions({
            localization: {
              priceFormatter: (price: number) => {
                return formatPrice(price, symbol);
              },
            },
          });
        }

        // Clear and set data synchronously to avoid race conditions
        seriesRef.current.setData(data);
        chartRef.current.timeScale().fitContent();
      }
    };

    loadData();

    // Poll for updates every 30 seconds - only update latest candle to avoid disrupting user interaction
    pollIntervalRef.current = setInterval(async () => {
      if (!seriesRef.current || !symbol) return;

      try {
        // Only fetch the latest few candles
        const intervalMs: Record<string, number> = {
          '1m': 60 * 1000,
          '5m': 5 * 60 * 1000,
          '15m': 15 * 60 * 1000,
          '1h': 60 * 60 * 1000,
          '4h': 4 * 60 * 60 * 1000,
          '1d': 24 * 60 * 60 * 1000,
          '1w': 7 * 24 * 60 * 60 * 1000,
        };

        const candleMs = intervalMs[selectedInterval] || intervalMs['1h'];
        const endTime = Date.now();
        const startTime = endTime - (5 * candleMs); // Only fetch last 5 candles

        const request = {
          type: 'candleSnapshot',
          req: {
            coin: symbol,
            interval: selectedInterval,
            startTime,
            endTime,
          },
        };

        const response = await fetch('https://api.hyperliquid.xyz/info', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(request),
        });

        if (response.ok) {
          const candles = await response.json();
          if (candles && candles.length > 0) {
            // Update each of the latest candles (handles partial candle updates)
            candles.forEach((c: any) => {
              seriesRef.current?.update({
                time: Math.floor(c.t / 1000),
                open: parseFloat(c.o),
                high: parseFloat(c.h),
                low: parseFloat(c.l),
                close: parseFloat(c.c),
              });
            });
          }
        }
      } catch (error) {
        // Silently ignore polling errors
      }
    }, 30000); // Poll every 30 seconds instead of 10

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, dexIndex, selectedInterval]);

  // Subscribe to visible range changes for infinite scroll
  useEffect(() => {
    if (!chartRef.current) return;

    const handleVisibleRangeChange = (range: any) => {
      if (!range || !seriesRef.current) return;

      // If we're close to the beginning (within 20 bars), load more
      if (range.from !== undefined && range.from < 20) {
        fetchOlderCandles();
      }
    };

    chartRef.current.timeScale().subscribeVisibleLogicalRangeChange(handleVisibleRangeChange);

    return () => {
      if (chartRef.current) {
        chartRef.current.timeScale().unsubscribeVisibleLogicalRangeChange(handleVisibleRangeChange);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, selectedInterval]);

  // Subscribe to live candle updates via WebSocket
  useEffect(() => {
    if (!symbol || !selectedInterval || !seriesRef.current) return;

    console.log('Subscribing to live candles:', symbol, selectedInterval);

    // Track current symbol to prevent race conditions
    const currentSymbol = symbol;
    const currentInterval = selectedInterval;

    const handleCandleUpdate = (candle: CandleData) => {
      // Only process updates for the current symbol/interval
      if (seriesRef.current && symbol === currentSymbol && selectedInterval === currentInterval) {
        // Update the latest candle
        seriesRef.current.update({
          time: candle.time,
          open: candle.open,
          high: candle.high,
          low: candle.low,
          close: candle.close,
        });
      }
    };

    candleWs.subscribe(symbol, selectedInterval, handleCandleUpdate);

    return () => {
      candleWs.unsubscribe(handleCandleUpdate);
    };
  }, [symbol, selectedInterval]);

  return (
    <div className="bg-[#0e0f10] rounded-md overflow-hidden border border-[#2d3748]" style={{ height: '641px' }}>
      <div className="px-4 border-b border-[#2d3748] flex items-center justify-between bg-[#0b0c0d]" style={{ minHeight: '40px' }}>
        <div className="flex items-center gap-3">
          <div className="flex gap-0.5">
            {[
              { label: '1m', value: '1m' },
              { label: '5m', value: '5m' },
              { label: '15m', value: '15m' },
              { label: '1H', value: '1h' },
              { label: '4H', value: '4h' },
              { label: '1D', value: '1d' },
              { label: '1W', value: '1w' },
            ].map((interval) => (
              <button
                key={interval.value}
                onClick={() => setSelectedInterval(interval.value)}
                className={`px-2 py-1 text-[11px] font-medium rounded transition-all cursor-pointer ${
                  selectedInterval === interval.value
                    ? 'bg-[#2962ff] text-white'
                    : 'text-[#6c7884] hover:text-[#b0b8c1] hover:bg-[#1a1a1a]'
                }`}
              >
                {interval.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div ref={chartContainerRef} className="bg-[#0e0f10] pb-2" />
    </div>
  );
}
